import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';
import { getUnitIdFromNodeId, normalizeTabStatus } from 'utils/AICreatorHelper';

import type IAICreatorBoMUnitsTabItem from '../interfaces/IAICreatorBoMUnitsTabItem';
import type IUseAICreatorBoMTabsParams from '../interfaces/IUseAICreatorBoMTabsParams';

const useAICreatorBoMTabs = ({
  open,
  selectedCards,
  session,
  sessionUnitStatusByUnitId,
  valueChainMapId,
  sessionId,
  updateAgentSessionBom,
  pendingAiUnitIds,
  initialActiveTabNodeId,
  onBadRequestError,
}: IUseAICreatorBoMTabsParams) => {
  const syncedInProgressStatusBomIdsRef = useRef<Set<number>>(new Set());
  const [activeTabNodeId, setActiveTabNodeId] = useState<string>('');
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [tabs, setTabs] = useState<IAICreatorBoMUnitsTabItem[]>([]);

  useEffect(() => {
    const pendingSet = new Set(pendingAiUnitIds);

    const nextTabs: IAICreatorBoMUnitsTabItem[] = selectedCards.map((card, index) => {
      const cardUnitId = getUnitIdFromNodeId(card.nodeId);
      const bomStatus = cardUnitId !== null ? sessionUnitStatusByUnitId.get(cardUnitId) : undefined;
      const selectedUnit = session?.selectedUnits.find((sessionUnit, sessionUnitIndex) => {
        if (cardUnitId !== null) {
          return sessionUnit.unitId === cardUnitId;
        }

        return sessionUnitIndex === index;
      });

      const serverStatus = normalizeTabStatus(bomStatus ?? selectedUnit?.status);
      const status = cardUnitId !== null && pendingSet.has(cardUnitId)
        ? VALUE_CHAIN_AI_AGENT.AI_STATUS
        : serverStatus;

      return {
        ...card,
        status,
      };
    });
    setTabs(nextTabs);
  }, [pendingAiUnitIds, selectedCards, session?.selectedUnits, sessionUnitStatusByUnitId]);

  useEffect(() => {
    if (!open) {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }

      return undefined;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setActiveTabNodeId('');
      return;
    }

    const hasActiveTab = tabs.some((tab) => tab.nodeId === activeTabNodeId);

    if (!hasActiveTab) {
      setActiveTabNodeId(tabs[0]?.nodeId || '');
    }
  }, [activeTabNodeId, open, tabs]);

  useEffect(() => {
    if (!open || !initialActiveTabNodeId) {
      return;
    }

    const hasRequestedTab = tabs.some((tab) => tab.nodeId === initialActiveTabNodeId);

    if (!hasRequestedTab || activeTabNodeId === initialActiveTabNodeId) {
      return;
    }

    setActiveTabNodeId(initialActiveTabNodeId);
  }, [activeTabNodeId, initialActiveTabNodeId, open, tabs]);

  const activeTab = useMemo(() => {
    const matched = tabs.find((tab) => tab.nodeId === activeTabNodeId);
    if (matched) {
      return matched;
    }
    // Do not fall back to tabs[0] when a tab is already selected: that mis-attributed
    // activeTab to the first tab and made only the first tab's status flicker (tabsForIndicator).
    if (!activeTabNodeId && tabs[0]) {
      return tabs[0];
    }
    return undefined;
  }, [activeTabNodeId, tabs]);

  const activeTabIndex = useMemo(
    () => tabs.findIndex((tab) => tab.nodeId === activeTab?.nodeId),
    [activeTab?.nodeId, tabs],
  );

  const isLastTab = activeTabIndex >= 0 && activeTabIndex === tabs.length - 1;

  const areAllTabsCompleted = useMemo(
    () => tabs.length > 0 && tabs.every((tab) => tab.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT),
    [tabs],
  );

  const isCreateAction = isLastTab && areAllTabsCompleted;

  const syncCurrentBomStatusInProgress = useCallback(async (bomId: number, hasItems: boolean) => {
    if (!valueChainMapId || !sessionId || !bomId || !activeTab) {
      return;
    }

    const nextStatus = hasItems
      ? VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
      : VALUE_CHAIN_AI_AGENT.STATUS_EMPTY;
    const nextStatusLegacy = hasItems
      ? VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS_LEGACY
      : VALUE_CHAIN_AI_AGENT.STATUS_EMPTY;

    if (activeTab.status === nextStatus || activeTab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS) {
      return;
    }

    setTabs((prevTabs) => prevTabs.map((tab) => (
      tab.nodeId === activeTab.nodeId
        ? { ...tab, status: nextStatus }
        : tab
    )));
    syncedInProgressStatusBomIdsRef.current.add(bomId);

    await updateAgentSessionBom({
      valueChainMapId,
      sessionId,
      bomId,
      body: {
        status: nextStatusLegacy,
      },
    }).unwrap().catch((error) => {
      syncedInProgressStatusBomIdsRef.current.delete(bomId);
      onBadRequestError(error);
    });
  }, [
    activeTab,
    onBadRequestError,
    sessionId,
    updateAgentSessionBom,
    valueChainMapId,
  ]);

  return {
    tabs,
    setTabs,
    activeTabNodeId,
    setActiveTabNodeId,
    isSelectAllChecked,
    setIsSelectAllChecked,
    activeTab,
    activeTabIndex,
    isCreateAction,
    syncCurrentBomStatusInProgress,
    syncedInProgressStatusBomIdsRef,
  };
};

export default useAICreatorBoMTabs;
